
export enum RepairStatus {
    Pending = 'pending',
    InProgress = 'in_progress',
    Completed = 'completed',
    Canceled = 'canceled',
    WaitingForParts = 'waiting_for_parts',
    Assigned = 'assigned',
    Resolved = 'resolved'
}

/**
 * What a repair is about.
 *
 * `Security` and `Locksmith` are distinct on purpose (#337). A tenant picks
 * "security" in the portal and means anything from a broken lock to a dead
 * intercom; folding that into `Locksmith` would rename their report for them.
 * The owner sets `Locksmith` once they know a locksmith is what it takes —
 * both route to `InterventionCategory.Locksmith` (see `CATEGORY_MAP`).
 *
 * Adding a value here is NOT enough: three allow-lists live outside this enum
 * and silently rewrite anything they do not recognise —
 * `cockpit/executors/createRepair.ts`, `ai/tools/interventions.tools.ts` and
 * `core/services/infra/validation.service.ts`.
 */
export enum RepairType {
    Electrical = 'electrical',
    Plumbing = 'plumbing',
    Structural = 'structural',
    Appliance = 'appliance',
    Heating = 'heating',
    Cooling = 'cooling',
    Roofing = 'roofing',
    Flooring = 'flooring',
    Security = 'security',
    Locksmith = 'locksmith',
    Other = 'other'
}

export enum RepairPriority {
    Low = 'low',
    Medium = 'medium',
    High = 'high',
    Emergency = 'emergency'
}

export enum RepairUrgency {
    ToPlan = "To plan",
    Normal = "Normal",
    Low = "Low", 
    Medium = "Medium",
    High = "High",
    Urgent = "Urgent"
}

/**
 * Outcome lifecycle of a single on-site visit (appointment). Mirrors the
 * field-service standard (Dynamics booking statuses / Salesforce appointment
 * lifecycle): a job can have several visits, and the repair's high-level
 * `status` is driven by the active visit. `incomplete` = the provider came but
 * the job isn't finished → a follow-up visit ("repassage") is needed.
 */
export type RepairVisitStatus =
    | 'proposed'
    | 'scheduled'
    | 'confirmed'
    | 'on_site'
    | 'completed'
    | 'no_show'
    | 'cancelled'
    | 'incomplete';

/**
 * Time granularity of an appointment. Providers rarely commit to an exact hour,
 * so a slot can be an exact time, a half-day (`am`/`pm`) or a whole day. When
 * not `exact`, `scheduledAt`'s time component is a canonical placeholder and the
 * UI renders the label from `precision`. Absent = legacy exact appointment.
 */
export type RepairVisitPrecision = 'exact' | 'am' | 'pm' | 'day';

export interface RepairVisit {
    /** Stable client-generated id (the repair owns the array). */
    id: string;
    /** Provider expected on site — defaults to the repair's `assignedTo`. */
    providerName?: string;
    /** Scheduled date + time of the appointment. */
    scheduledAt: Date;
    status: RepairVisitStatus;
    /**
     * Time granularity of the slot. Defaults to `exact` when absent (legacy
     * appointments). `am`/`pm` = half-day, `day` = whole day.
     */
    precision?: RepairVisitPrecision;
    /**
     * Who put this slot forward. `provider` = a candidate slot proposed from the
     * provider portal (paired with `status: 'proposed'`), awaiting owner
     * confirmation; `owner` = scheduled directly by the property manager.
     */
    proposedBy?: 'provider' | 'owner';
    /** Free-text note (access instructions, outcome detail). */
    note?: string;
    createdAt: Date;
    /** When a terminal outcome (completed / no_show / incomplete) was recorded. */
    outcomeAt?: Date;
}

export class Repair {
    repairUID?: string;
    propertyUID!: string;
    propertyName?: string;
    // Syndic context — a repair raised on a building's common parts (no unit) or
    // on a specific unit. Optional and additive: PM repairs leave these unset and
    // keep using propertyUID. For syndic-scoped repairs propertyUID is an empty
    // string and the location is read from building/unit instead.
    buildingUID?: string;
    buildingName?: string;
    unitUID?: string;
    unitName?: string;
    title!: string;
    description!: string;
    type: RepairType = RepairType.Other;
    status: RepairStatus = RepairStatus.Pending;
    priority: RepairPriority = RepairPriority.Medium;
    urgency: RepairUrgency = RepairUrgency.Normal;
    reportedDate: Date = new Date();
    scheduledDate?: Date;
    completedDate?: Date;
    estimatedCost?: number;
    actualCost?: number;
    assignedTo?: string;
    reportedBy?: string; // Can be owner or tenant UID
    notes?: string;
    attachments?: string[]; // URLs to documents/images
    ownerUID!: string;
    createdAt: Date = new Date();
    updatedAt: Date = new Date();

    // Bridge to the unified Intervention model. Set the first time a work
    // request is sent through `inviteProviderToIntervention`; from that
    // moment on, the assisted follow-up panel reads live signals from the
    // linked Intervention + ServiceRequest documents.
    interventionUID?: string;

    // Scheduled on-site visits (appointments). Additive: legacy repairs have
    // none and keep using `scheduledDate`. Each "repassage" appends a visit, so
    // the array is the full appointment history; the active visit drives the
    // high-level `status`. See `repair-visit.util.ts` for the derivation.
    visits?: RepairVisit[];

    // Cockpit provenance — stamped by the `createRepair` / `createCase`
    // executors when this repair was opened from an email-triggered workflow.
    // Enables the "Issu du Cockpit" back-link + workflow-status panel on the
    // repair detail page. Absent on manually-created repairs.
    origin?: 'cockpit';
    cardId?: string;
    effectId?: string;

    // Half-days the tenant offered for the visit, mirrored from their portal
    // request by `onTenantRepairNotificationUpdated` (#619). `date` is
    // `YYYY-MM-DD`. Absent unless the repair came from a tenant request.
    tenantAvailability?: { date: string; period: 'morning' | 'afternoon' | 'evening' }[];

    constructor(propertyUID: string, title: string, description: string, reportedDate: Date) {
        this.propertyUID = propertyUID;
        this.title = title;
        this.description = description;
        this.reportedDate = reportedDate;
    }
}